import './trivia-fan.css';
import { TypedFanEngagement } from '@/utils/engagementTypeUtils';
import {
  TriviaRoundStatus,
  TriviaViewConfig,
  TriviaViewData,
  useTriviaJoinMutation,
  useTriviaMyAnswersQuery,
  useTriviaSubmitAnswerMutation,
} from '@/generated/graphql';
import { useAuth } from '@/hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { LetterTile } from '../LetterTile';
import { useTriviaFit } from '../useTriviaFit';
import { countdownRatio, useCountdown } from '../useCountdown';
import { formatCountdown } from '../constants';
import {
  CheckIcon,
  ClockIcon,
  LockIcon,
  TrophyIcon,
  XIcon,
} from '../TriviaIcons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  choiceIdForQuestion,
  choiceIdFromSubmitResult,
  resolveTriviaFanPick,
  shouldShowTriviaFanMissed,
} from '../fanPick';

export function FanTriviaEngagement({
  engagement,
}: {
  engagement: TypedFanEngagement<TriviaViewConfig, TriviaViewData>;
}) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preview = engagement.id === 'preview-trivia';
  const previewPick = preview ? params.get('pick') : null;
  const previewFrame = params.get('frame');
  const view = engagement.viewData;
  const config = engagement.viewConfig;
  const status = view?.status ?? TriviaRoundStatus.Idle;
  const questions = config?.questions ?? [];
  const index = view?.questionIndex ?? 0;
  const question = questions[index];
  const [join] = useTriviaJoinMutation();
  const { data: myData, refetch } = useTriviaMyAnswersQuery({
    variables: { engagementId: engagement.id },
    skip: !engagement.id || preview,
  });
  const progress = myData?.triviaMyAnswers;
  const [submitAnswer, submitState] = useTriviaSubmitAnswerMutation();
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [rejected, setRejected] = useState(false);
  const seenReveal = useRef(new Set<string>());

  useEffect(() => {
    if (!engagement.id || preview) return;
    void join({ variables: { engagementId: engagement.id } });
  }, [engagement.id, join, preview]);

  useEffect(() => {
    if (preview) return;
    void refetch();
  }, [preview, refetch, status, question?.id, index]);

  useEffect(() => {
    if (preview) return;
    const onReconnect = () => {
      void refetch();
    };
    window.addEventListener('online', onReconnect);
    window.addEventListener('focus', onReconnect);
    return () => {
      window.removeEventListener('online', onReconnect);
      window.removeEventListener('focus', onReconnect);
    };
  }, [preview, refetch]);

  useEffect(() => {
    if (status === TriviaRoundStatus.Idle) {
      seenReveal.current.clear();
    }
    if (status === TriviaRoundStatus.Revealed && question?.id) {
      seenReveal.current.add(question.id);
    }
  }, [status, question?.id]);

  useEffect(() => {
    setOptimistic(null);
    setRejected(false);
  }, [question?.id, status]);

  const myByQuestion = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of progress?.answers ?? []) {
      map.set(row.questionId, row.choiceId);
    }
    return map;
  }, [progress]);

  const myPick = question
    ? resolveTriviaFanPick({
        previewPick,
        serverChoiceId: myByQuestion.get(question.id) ?? null,
        optimisticChoiceId: optimistic,
      })
    : null;
  const { remainingMs } = useCountdown(view?.questionEndsAt);
  const timerMs = question?.timerMs ?? config?.defaultTimerMs ?? 30_000;
  const fitRef = useTriviaFit([question?.id, status, question?.text]);
  const score = preview
    ? (view?.leaderboard?.find((row) => row.userId === 'me')?.score ??
      view?.leaderboard?.[0]?.score ??
      0)
    : (progress?.score ?? 0);

  const handlePick = async (choiceId: string) => {
    if (!question || myPick || status !== TriviaRoundStatus.QuestionOpen)
      return;
    setOptimistic(choiceId);
    setRejected(false);
    try {
      const result = await submitAnswer({
        variables: {
          engagementId: engagement.id,
          questionId: question.id,
          choiceId,
        },
      });
      const kept = choiceIdFromSubmitResult(result.data);
      if (kept) setOptimistic(kept);
      const refreshed = await refetch();
      const serverPick = choiceIdForQuestion(
        refreshed.data?.triviaMyAnswers?.answers,
        question.id,
      );
      if (serverPick) setOptimistic(null);
    } catch {
      const refreshed = await refetch();
      const serverPick = choiceIdForQuestion(
        refreshed.data?.triviaMyAnswers?.answers,
        question.id,
      );
      if (serverPick) {
        setOptimistic(serverPick);
        setRejected(false);
      } else {
        setOptimistic(null);
        setRejected(true);
      }
    }
  };

  const answeredCount = myByQuestion.size;
  const scoredByQuestion = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of progress?.scored ?? []) {
      map.set(row.questionId, row.correctChoiceId);
    }
    return map;
  }, [progress]);
  const currentCorrectId = question
    ? (view?.correctChoiceId ?? scoredByQuestion.get(question.id) ?? null)
    : null;
  const showFanReveal =
    !!question &&
    (status === TriviaRoundStatus.Revealed ||
      (status === TriviaRoundStatus.QuestionClosed &&
        scoredByQuestion.has(question.id)));
  const previousQuestion = index > 0 ? questions[index - 1] : undefined;
  const skipResultCorrectId = previousQuestion
    ? scoredByQuestion.get(previousQuestion.id)
    : undefined;
  const showSkipResultOnNext =
    status === TriviaRoundStatus.QuestionOpen &&
    !!previousQuestion &&
    !!skipResultCorrectId &&
    !seenReveal.current.has(previousQuestion.id);

  const nickname =
    user?.name?.trim() && user.name.trim().toLowerCase() !== 'you'
      ? user.name.trim()
      : preview
        ? 'Test Wombat'
        : 'Player';
  const idleInstructions = (() => {
    const raw = engagement.description || config?.instructions || '';
    if (!raw || /get ready/i.test(raw)) {
      return 'Trivia starts soon. Questions pop up right here, so keep this page open.';
    }
    return raw;
  })();
  const leaderboard = view?.leaderboard ?? [];
  const mine = preview
    ? (leaderboard.find((row) => row.userId === 'me') ?? leaderboard[0])
    : {
        score,
        rank: progress?.rank ?? 0,
        tied: false,
      };
  const answeredNothing = preview
    ? previewFrame === 'fan-complete-empty'
    : answeredCount === 0;
  const coWinners = leaderboard.filter((row) => row.rank === 1);

  return (
    <div className="trivia-fan">
      <header className="trivia-fan-top">
        <span className="nm">
          <i />
          {engagement.title}
        </span>
        {status === TriviaRoundStatus.Idle ? (
          <span className="sc">{nickname}</span>
        ) : (
          <span className="sc">
            Score <b>{mine?.score ?? score}</b>
          </span>
        )}
      </header>

      {status === TriviaRoundStatus.Idle ||
      ((status === TriviaRoundStatus.QuestionClosed ||
        status === TriviaRoundStatus.Revealed) &&
        !question) ? (
        <Idle name={nickname} instructions={idleInstructions} />
      ) : null}

      {showSkipResultOnNext && previousQuestion ? (
        <SkipResultBanner
          question={previousQuestion}
          myPick={myByQuestion.get(previousQuestion.id) ?? null}
          correctId={skipResultCorrectId}
          score={mine?.score ?? score}
        />
      ) : null}

      {status === TriviaRoundStatus.QuestionOpen && question && !myPick ? (
        <div className="trivia-fan-body" ref={fitRef} data-fitroot>
          <div className="f-meta">
            <span>
              Question {index + 1} of {questions.length}
            </span>
            {view?.questionEndsAt ? (
              <span className="t">{formatCountdown(remainingMs ?? 0)}</span>
            ) : null}
          </div>
          {view?.questionEndsAt ? (
            <div className="f-bar">
              <i
                style={{
                  width: `${countdownRatio(remainingMs, timerMs) * 100}%`,
                }}
              />
            </div>
          ) : null}
          <p className="f-q" data-base="24">
            {question.text}
          </p>
          <div className="f-choices">
            {question.choices.map((choice, i) => (
              <button
                key={`${question.id}:${choice.id}:${i}`}
                type="button"
                className="f-ch"
                onClick={() => handlePick(choice.id)}
              >
                <LetterTile index={i} size={44} />
                <span className="tx" data-base="16">
                  {choice.text}
                </span>
              </button>
            ))}
          </div>
          <p className="f-help">
            Tap an answer. You can&apos;t change it after.
          </p>
        </div>
      ) : null}

      {(status === TriviaRoundStatus.QuestionOpen ||
        status === TriviaRoundStatus.QuestionClosed) &&
      myPick &&
      question &&
      !rejected &&
      !showFanReveal ? (
        <Locked
          question={question}
          myPick={myPick}
          submitting={submitState.loading && !!optimistic}
          closed={status === TriviaRoundStatus.QuestionClosed}
          index={index}
          total={questions.length}
          remainingMs={remainingMs}
          timerMs={timerMs}
          endsAt={view?.questionEndsAt}
        />
      ) : null}

      {shouldShowTriviaFanMissed({
        rejected,
        status,
        myPick,
        showFanReveal,
      }) && question ? (
        <Missed
          score={mine?.score ?? score}
          questionText={question.text}
          index={index}
          total={questions.length}
        />
      ) : null}

      {showFanReveal && question ? (
        <Revealed
          question={question}
          myPick={myPick}
          correctId={currentCorrectId}
          score={mine?.score ?? score}
          index={index}
          total={questions.length}
        />
      ) : null}

      {status === TriviaRoundStatus.RoundComplete ? (
        answeredNothing ? (
          <CompleteEmpty title={engagement.title} />
        ) : mine && mine.rank === 1 && coWinners.length > 1 ? (
          <CompleteCoWinner
            mine={mine}
            others={coWinners}
            total={view?.playerCount ?? 0}
            questions={questions.length}
            name={user?.name || 'you'}
          />
        ) : (
          <Complete
            mine={mine}
            total={view?.playerCount ?? 0}
            questions={questions.length}
            title={engagement.title}
            topName={leaderboard[0]?.name}
            topScore={leaderboard[0]?.score}
          />
        )
      ) : null}
    </div>
  );
}

function Idle({ name, instructions }: { name: string; instructions: string }) {
  return (
    <div className="trivia-fan-body f-center">
      <div className="brand-bar" />
      <span className="pill o">You&apos;re in</span>
      <h1 className="f-title">Get ready!</h1>
      <p className="f-sub">{instructions}</p>
      <div className="fcard" style={{ marginTop: 28 }}>
        <div className="k">Playing as</div>
        <div className="f-name">{name}</div>
      </div>
      <div className="fcard" style={{ marginTop: 12 }}>
        <div className="k">How it works</div>
        <div className="f-how">
          <div>
            <ClockIcon size={18} />
            Tap an answer before time runs out
          </div>
          <div>
            <LockIcon size={18} />
            Your first tap locks in, so choose wisely
          </div>
          <div>
            <TrophyIcon size={18} />
            +1 point for every right answer
          </div>
        </div>
      </div>
      <p className="hsfoot">HappiScreen</p>
    </div>
  );
}

function Locked({
  question,
  myPick,
  submitting,
  closed,
  index,
  total,
  remainingMs,
  timerMs,
  endsAt,
}: {
  question: { text: string; choices: Array<{ id: string; text: string }> };
  myPick: string;
  submitting: boolean;
  closed: boolean;
  index: number;
  total: number;
  remainingMs: number | null;
  timerMs: number;
  endsAt?: string | Date | null;
}) {
  return (
    <div className="trivia-fan-body">
      <div className="f-meta">
        <span>
          Question {index + 1} of {total}
        </span>
        {!closed && endsAt ? (
          <span className="t">
            <ClockIcon size={14} /> {formatCountdown(remainingMs ?? 0)}
          </span>
        ) : (
          <span className="t">Answers locked</span>
        )}
      </div>
      {!closed && endsAt ? (
        <div className="f-bar">
          <i
            style={{
              width: `${countdownRatio(remainingMs, timerMs) * 100}%`,
            }}
          />
        </div>
      ) : null}
      <div className="f-lock-banner">
        <div className="f-lock-ico">
          <LockIcon size={22} />
        </div>
        <div>
          <div className="f-lock-title">Locked in!</div>
          <div className="f-sub">
            Hang tight. The answer shows after time&apos;s up.
          </div>
        </div>
      </div>
      <p className="f-q" data-base="20">
        {question.text}
      </p>
      <div className="f-choices">
        {question.choices.map((choice, i) => (
          <div
            key={`${question.text}:${choice.id}:${i}`}
            className={cn(
              'f-ch',
              choice.id === myPick && 'sel',
              choice.id !== myPick && 'dim',
            )}
          >
            <LetterTile index={i} size={44} />
            <span className="tx" data-base="18">
              {choice.text}
            </span>
            {choice.id === myPick ? (
              <span className="tag">
                <LockIcon size={14} /> {submitting ? '…' : 'Your pick'}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      <p className="f-help">Eyes on the big screen</p>
    </div>
  );
}

function Missed({
  score,
  questionText,
  index,
  total,
}: {
  score: number;
  questionText: string;
  index: number;
  total: number;
}) {
  return (
    <div className="trivia-fan-body f-center">
      <div className="bigmark n">
        <ClockIcon size={52} />
      </div>
      <span className="pill n" style={{ marginTop: 22 }}>
        Answers locked
      </span>
      <h1 className="f-title">Time&apos;s up!</h1>
      <p className="f-sub">
        You didn&apos;t get an answer in on this one. Catch the next question!
      </p>
      <div className="fcard" style={{ marginTop: 24 }}>
        <div className="k">
          Question {index + 1} of {total}
        </div>
        <div className="f-recap" data-base="17">
          {questionText}
        </div>
      </div>
      <p className="hsfoot">No points lost. Your score stays at {score}.</p>
    </div>
  );
}

function SkipResultBanner({
  question,
  myPick,
  correctId,
  score,
}: {
  question: { choices: Array<{ id: string; text: string }> };
  myPick: string | null;
  correctId?: string | null;
  score: number;
}) {
  const correct = myPick && myPick === correctId;
  const answer = question.choices.find((choice) => choice.id === correctId);
  return (
    <div className="trivia-fan-body" style={{ paddingBottom: 0 }}>
      <span className={cn('pill', correct ? 'g' : myPick ? 'r' : 'n')}>
        {correct ? 'CORRECT' : myPick ? 'NOT QUITE' : 'MISSED'}
      </span>
      <p className="f-sub" style={{ margin: '8px 0 0' }}>
        {correct
          ? `Nice one! +1 · Your score is now ${score}.`
          : answer
            ? `The answer was ${answer.text}.`
            : 'The answer is on your phone.'}
      </p>
    </div>
  );
}

function Revealed({
  question,
  myPick,
  correctId,
  score,
  index,
  total,
}: {
  question: { text: string; choices: Array<{ id: string; text: string }> };
  myPick: string | null;
  correctId?: string | null;
  score: number;
  index: number;
  total: number;
}) {
  const correct = myPick && myPick === correctId;
  const missed = !myPick;
  return (
    <div className="trivia-fan-body f-center">
      <div className={cn('bigmark', correct ? 'g' : missed ? 'n' : 'r')}>
        {correct ? (
          <CheckIcon size={60} />
        ) : missed ? (
          <ClockIcon size={52} />
        ) : (
          <XIcon size={56} />
        )}
      </div>
      <span
        className={cn('pill', correct ? 'g' : missed ? 'n' : 'r')}
        style={{ marginTop: 22 }}
      >
        {correct ? 'Correct' : missed ? 'Missed' : 'Not quite'}
      </span>
      <h1 className="f-title">
        {correct ? 'Nice one! +1' : missed ? 'Missed this one' : 'So close!'}
      </h1>
      <p className="f-sub">
        {correct ? (
          <>
            Your score is now <b className="f-strong">{score}</b>.
          </>
        ) : missed ? (
          'The answer is highlighted below.'
        ) : (
          "Shake it off. The next one's yours."
        )}
      </p>
      <div className="fcard" style={{ marginTop: 24 }}>
        <div className="k">
          Question {index + 1} of {total}
        </div>
        <div className="f-recap" data-base="17" style={{ marginBottom: 12 }}>
          {question.text}
        </div>
        <div className="f-choices">
          {question.choices.map((choice, i) => {
            const isCorrect = choice.id === correctId;
            const isPick = choice.id === myPick;
            if (!isCorrect && !isPick) return null;
            return (
              <div
                key={choice.id}
                className={cn(
                  'f-ch',
                  isCorrect && 'correct',
                  isPick && !isCorrect && 'wrong',
                )}
                style={{ minHeight: 58 }}
              >
                <LetterTile index={i} size={44} />
                <span className="tx" data-base="17">
                  {choice.text}
                </span>
                <span className={cn('tag', isCorrect ? 'ok' : 'bad')}>
                  {isCorrect ? <CheckIcon size={18} /> : <XIcon size={18} />}
                  {isPick ? 'Your pick' : 'Answer'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="hsfoot">
        {correct
          ? 'Next question coming up…'
          : missed
            ? `Score stays at ${score} · Next question coming up…`
            : `Score stays at ${score} · Next question coming up…`}
      </p>
    </div>
  );
}

function Complete({
  mine,
  total,
  questions,
  title,
  topName,
  topScore,
}: {
  mine?: { score: number; rank: number; tied: boolean } | null;
  total: number;
  questions: number;
  title: string;
  topName?: string;
  topScore?: number;
}) {
  return (
    <div className="trivia-fan-body f-center">
      <div className="bigmark o">
        <TrophyIcon size={52} />
      </div>
      <span className="pill o" style={{ marginTop: 20 }}>
        Round over
      </span>
      <h1 className="f-title">
        You got {mine?.score ?? 0} of {questions}!
      </h1>
      <p className="f-sub">Thanks for playing {title}.</p>
      <div className="fcard" style={{ marginTop: 24, textAlign: 'center' }}>
        <div className="k">Your rank</div>
        <div
          style={{
            fontSize: 56,
            fontWeight: 900,
            lineHeight: 1,
            color: '#ff6b3d',
          }}
        >
          #{mine?.rank ?? '—'}
        </div>
        <div className="f-sub" style={{ marginTop: 6 }}>
          {mine?.tied ? `Tied, ` : null}
          out of {total} players
        </div>
      </div>
      {topName != null && topScore != null ? (
        <div
          className="fcard"
          style={{
            marginTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span className="f-sub">Top score</span>
          <span style={{ fontWeight: 700 }}>
            {topName} · {topScore}
          </span>
        </div>
      ) : null}
      <p className="hsfoot">HappiScreen</p>
    </div>
  );
}

function CompleteCoWinner({
  mine,
  others,
  total,
  questions,
  name,
}: {
  mine: { score: number; rank: number };
  others: Array<{ name: string; userId: string }>;
  total: number;
  questions: number;
  name: string;
}) {
  const names = others.map((row) => (row.name === name ? 'you' : row.name));
  return (
    <div className="trivia-fan-body f-center">
      <div className="bigmark gold">
        <TrophyIcon size={52} />
      </div>
      <span className="pill gold" style={{ marginTop: 20 }}>
        Co-winner
      </span>
      <h1 className="f-title">You tied for 1st!</h1>
      <p className="f-sub">
        {mine.score} of {questions} right. You share the win.
      </p>
      <p className="f-sub">
        #1, tied with {others.length - 1} other player, out of {total}
      </p>
      <p className="f-sub">Co-winners: {names.join(' & ')}</p>
    </div>
  );
}

function CompleteEmpty({ title }: { title: string }) {
  return (
    <div className="trivia-fan-body f-center">
      <span className="pill o">ROUND OVER</span>
      <h1 className="f-title">Thanks for playing!</h1>
      <p className="f-sub">
        {title} is all wrapped up. Eyes on the big screen for the winners.
      </p>
    </div>
  );
}
